'use client';

import { useEffect, useState } from 'react';
import { api, useMutation } from '@/lib/convex';
import {
  detectBrowserTimezone,
  shouldSyncBrowserTimezone,
  timezoneSyncAttempt,
  timezoneSyncRetryDelayMs,
  type TimezoneSource,
} from '@/lib/regional-timezone';

type SyncSettings = {
  timezone?: string;
  timezoneSource?: TimezoneSource;
};

/**
 * Saves the device zone over an unset default. A rejection retries until the
 * cap. A resolved false is final: the server left the profile unchanged.
 * One success is enough, and a choice is never overwritten.
 */
export function useSyncBrowserTimezone(settings: SyncSettings | null): {
  pending: boolean;
} {
  const syncDetectedTimezone = useMutation(
    api.users.mutations.syncDetectedTimezone
  );
  const [attempt, setAttempt] = useState(0);
  const [stoppedFor, setStoppedFor] = useState<string | null>(null);
  const browserTimezone = settings ? detectBrowserTimezone() : null;
  const needsSync = Boolean(
    settings &&
    browserTimezone &&
    shouldSyncBrowserTimezone(settings, browserTimezone)
  );
  const unchanged = needsSync && stoppedFor === browserTimezone;

  useEffect(() => {
    if (!needsSync || !browserTimezone || unchanged) return;
    if (timezoneSyncAttempt(attempt) === 'stop') return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    void syncDetectedTimezone({ timezone: browserTimezone }).then(
      (saved) => {
        if (cancelled || saved) return;
        setStoppedFor(browserTimezone);
      },
      () => {
        if (cancelled) return;
        retry = setTimeout(() => {
          if (!cancelled) setAttempt((current) => current + 1);
        }, timezoneSyncRetryDelayMs(attempt));
      }
    );
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [attempt, browserTimezone, needsSync, syncDetectedTimezone, unchanged]);

  return {
    pending: needsSync && !unchanged && timezoneSyncAttempt(attempt) === 'send',
  };
}
